import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageCircle, Loader2, CheckCircle2, RotateCw, ArrowLeft } from "lucide-react";

const OTP_LENGTH = 6;

/**
 * Premium 6-digit OTP popup.
 * - `onVerify(code)` MUST return a promise that resolves on success or throws on failure.
 * - `onResend()` MUST return a promise; component handles the 60s cooldown.
 * - `onChangeNumber()` (optional) lets the user go back to edit their number.
 */
export const OtpModal = ({
  open,
  onClose,
  mobileMasked,
  title = "Verify your WhatsApp number",
  description,
  onVerify,
  onResend,
  onChangeNumber,
  testIdPrefix = "otp",
}) => {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const inputs = useRef([]);

  useEffect(() => {
    if (open) {
      setDigits(Array(OTP_LENGTH).fill(""));
      setError("");
      setSuccess(false);
      setCooldown(60);
      setTimeout(() => inputs.current[0]?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open || cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown, open]);

  const setDigit = (i, v) => {
    if (!/^[0-9]?$/.test(v)) return;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError("");
    if (v && i < OTP_LENGTH - 1) inputs.current[i + 1]?.focus();
    if (next.every((d) => d) && next.join("").length === OTP_LENGTH) {
      doVerify(next.join(""));
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = text.split("").concat(Array(OTP_LENGTH - text.length).fill(""));
    setDigits(next);
    setTimeout(() => inputs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus(), 10);
    if (text.length === OTP_LENGTH) doVerify(text);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const doVerify = async (code) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await onVerify(code);
      setSuccess(true);
      setTimeout(() => onClose?.(), 900);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Verification failed";
      setError(typeof msg === "string" ? msg : "Verification failed");
      setDigits(Array(OTP_LENGTH).fill(""));
      setTimeout(() => inputs.current[0]?.focus(), 50);
    } finally {
      setBusy(false);
    }
  };

  const doResend = async () => {
    if (cooldown > 0 || !onResend) return;
    setError("");
    try {
      await onResend();
      setCooldown(60);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not resend OTP";
      setError(typeof msg === "string" ? msg : "Could not resend OTP");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-3">
            <MessageCircle className="h-7 w-7 text-white" />
          </div>
          <DialogTitle className="text-center text-xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {description || (
              <>We&apos;ve sent a 6-digit code to <b>WhatsApp +91 {mobileMasked || "•••• ••••"}</b></>
            )}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="ok"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-2 py-4"
              data-testid={`${testIdPrefix}-success`}
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <div className="font-bold text-emerald-700">Verified!</div>
            </motion.div>
          ) : (
            <motion.div key="input" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center justify-center gap-2 mt-2" onPaste={onPaste} data-testid={`${testIdPrefix}-inputs`}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputs.current[i] = el)}
                    value={d}
                    onChange={(e) => setDigit(i, e.target.value.replace(/\D/g, "").slice(-1))}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    disabled={busy}
                    data-testid={`${testIdPrefix}-digit-${i}`}
                    className="h-12 w-11 rounded-xl border border-slate-200 bg-white text-center text-xl font-extrabold tabular-nums tracking-tight focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-300 disabled:opacity-60"
                  />
                ))}
              </div>

              {error && (
                <div className="mt-3 text-center text-[12.5px] font-semibold text-rose-600" data-testid={`${testIdPrefix}-error`}>
                  {error}
                </div>
              )}

              <div className="mt-5 flex items-center justify-between text-[12.5px]">
                <button type="button" onClick={onChangeNumber} disabled={!onChangeNumber || busy}
                  data-testid={`${testIdPrefix}-change-number`}
                  className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 disabled:opacity-40">
                  <ArrowLeft className="h-3.5 w-3.5" /> Change number
                </button>
                <button type="button" onClick={doResend} disabled={cooldown > 0 || busy}
                  data-testid={`${testIdPrefix}-resend`}
                  className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:text-emerald-800 disabled:text-slate-400 disabled:hover:text-slate-400">
                  <RotateCw className="h-3.5 w-3.5" />
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                </button>
              </div>

              <Button
                onClick={() => doVerify(digits.join(""))}
                disabled={busy || digits.some((d) => !d)}
                data-testid={`${testIdPrefix}-verify-btn`}
                className="mt-5 w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</> : "Verify"}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default OtpModal;
